import { useTranslation } from 'react-i18next';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { PATIENT_FIELD_KEYS } from '@/lib/patientReports/template';
import {
  createReportSection,
  moveField,
  moveSection,
  removeSection,
  reorderSections,
  toggleField,
  updateSection,
  type PatientFieldKey,
  type ReportSection,
  type ReportSectionKind,
} from '@/lib/patientReports/template';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const FIELD_LABEL: Record<PatientFieldKey, string> = {
  name: 'patientReports.field_name',
  cpr: 'patientReports.field_cpr',
  contact: 'patientReports.field_contact',
  address: 'patientReports.field_address',
  job_title: 'patientReports.field_job',
  cave: 'patientReports.field_cave',
  notes: 'patientReports.field_notes',
  diagnosis: 'patientReports.field_diagnosis',
  pat_number: 'patientReports.field_pat_number',
  locale: 'patientReports.field_locale',
};

export function ReportSectionCards({
  sections,
  onChange,
  tags,
  questions,
  formOptions,
  disabled,
}: {
  sections: ReportSection[];
  onChange: (sections: ReportSection[]) => void;
  tags: readonly { id: string; name: string }[];
  questions: Readonly<Record<string, readonly { id: string; text: string }[]>>;
  formOptions?: readonly { id: string; name: string }[];
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const add = (kind: ReportSectionKind, headingKey: string) => {
    onChange([...sections, createReportSection(kind, t(headingKey))]);
  };

  return (
    <div>
      <div className="mb-s3 flex flex-wrap gap-s2">
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => add('patient_fields', 'patientReports.add_patient')}
        >
          {t('patientReports.add_patient')}
        </Button>
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => add('journal', 'patientReports.add_journal')}
        >
          {t('patientReports.add_journal')}
        </Button>
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => add('markings', 'patientReports.add_markings')}
        >
          {t('patientReports.add_markings')}
        </Button>
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => add('questionnaire', 'patientReports.add_questionnaire')}
        >
          {t('patientReports.add_questionnaire')}
        </Button>
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => add('text', 'patientReports.text_heading')}
        >
          {t('patientReports.add_text')}
        </Button>
      </div>
      {sections.length === 0 ? (
        <p className={cn(TYPE.body, 'text-ds-muted')}>{t('patientReports.document_empty')}</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event: DragEndEvent) => {
            const overId = event.over ? String(event.over.id) : null;
            if (!overId) return;
            onChange(reorderSections(sections, String(event.active.id), overId));
          }}
        >
          <SortableContext
            items={sections.map((section) => section.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-s3">
              {sections.map((section, index) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  index={index}
                  count={sections.length}
                  tags={tags}
                  questions={questions[section.id] ?? []}
                  formOptions={formOptions}
                  disabled={disabled}
                  onChange={(next) => onChange(updateSection(sections, section.id, next))}
                  onMove={(direction) => onChange(moveSection(sections, section.id, direction))}
                  onRemove={() => onChange(removeSection(sections, section.id))}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function SectionCard({
  section,
  index,
  count,
  tags,
  questions,
  formOptions,
  disabled,
  onChange,
  onMove,
  onRemove,
}: {
  section: ReportSection;
  index: number;
  count: number;
  tags: readonly { id: string; name: string }[];
  questions: readonly { id: string; text: string }[];
  formOptions?: readonly { id: string; name: string }[];
  disabled?: boolean;
  onChange: (section: ReportSection) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const sortable = useSortable({ id: section.id, disabled });
  return (
    <article
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      className={cn(
        'rounded-ctl border border-ds-line bg-ds-bg p-s3',
        sortable.isDragging && 'opacity-70',
      )}
    >
      <div className="mb-s2 flex items-center gap-s2">
        <button
          type="button"
          className="cursor-grab text-ds-muted"
          aria-label={t('patientReports.drag')}
          {...sortable.attributes}
          {...sortable.listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className={cn(TYPE.small, 'text-ds-muted')}>{index + 1}</span>
        <div className="ml-auto flex items-center gap-s1">
          <button
            type="button"
            aria-label={t('patientReports.move_up')}
            disabled={disabled || index === 0}
            onClick={() => onMove(-1)}
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={t('patientReports.move_down')}
            disabled={disabled || index === count - 1}
            onClick={() => onMove(1)}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={cn(TYPE.small, 'text-ds-red-ink')}
            disabled={disabled}
            onClick={onRemove}
          >
            {t('patientReports.remove')}
          </button>
        </div>
      </div>
      <Input
        label={t('patientReports.heading')}
        value={section.heading}
        disabled={disabled}
        onChange={(event) => onChange({ ...section, heading: event.target.value })}
      />
      {section.kind === 'patient_fields' ? (
        <div className="mt-s3">
          <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.fields_hint')}</p>
          <div className="mt-s2 flex flex-col gap-s1">
            {PATIENT_FIELD_KEYS.map((field) => {
              const selected = section.fields.includes(field);
              const fieldIndex = section.fields.indexOf(field);
              return (
                <div key={field} className="flex items-center gap-s2">
                  <Checkbox
                    label={t(FIELD_LABEL[field])}
                    checked={selected}
                    disabled={disabled}
                    onChange={(event) =>
                      onChange({
                        ...section,
                        fields: toggleField(section.fields, field, event.target.checked),
                      })
                    }
                  />
                  {selected ? (
                    <span className="ml-auto flex gap-s1">
                      <button
                        type="button"
                        aria-label={t('patientReports.move_up')}
                        disabled={disabled || fieldIndex === 0}
                        onClick={() =>
                          onChange({ ...section, fields: moveField(section.fields, field, -1) })
                        }
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={t('patientReports.move_down')}
                        disabled={disabled || fieldIndex === section.fields.length - 1}
                        onClick={() =>
                          onChange({ ...section, fields: moveField(section.fields, field, 1) })
                        }
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      {section.kind === 'journal' ? (
        <div className="mt-s3 grid gap-s3 sm:grid-cols-2">
          <Input
            label={t('patientReports.journal_limit')}
            type="number"
            min={1}
            max={50}
            placeholder={t('patientReports.journal_limit_all')}
            disabled={disabled}
            value={section.limit ?? ''}
            onChange={(event) => {
              const parsed = Number(event.target.value);
              onChange({
                ...section,
                noteIds: null,
                limit:
                  Number.isFinite(parsed) && parsed >= 1 ? Math.min(50, Math.trunc(parsed)) : null,
              });
            }}
          />
          <Select
            label={t('patientReports.order_label')}
            disabled={disabled}
            value={section.order}
            onChange={(event) =>
              onChange({
                ...section,
                noteIds: null,
                order: event.target.value === 'oldest' ? 'oldest' : 'newest',
              })
            }
          >
            <option value="newest">{t('patientReports.order_newest')}</option>
            <option value="oldest">{t('patientReports.order_oldest')}</option>
          </Select>
        </div>
      ) : null}
      {section.kind === 'markings' ? (
        <div className="mt-s3 flex flex-col gap-s3">
          <Select
            label={t('patientReports.order_label')}
            disabled={disabled}
            value={section.order}
            onChange={(event) =>
              onChange({
                ...section,
                order:
                  event.target.value === 'oldest' || event.target.value === 'file'
                    ? event.target.value
                    : 'newest',
              })
            }
          >
            <option value="newest">{t('patientReports.order_newest')}</option>
            <option value="oldest">{t('patientReports.order_oldest')}</option>
            <option value="file">{t('patientReports.order_file')}</option>
          </Select>
          <div>
            <p className={cn(TYPE.small, 'font-semibold text-ds-text')}>
              {t('patientReports.tags_label')}
            </p>
            <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.tags_all')}</p>
            <div className="mt-s2 flex flex-col gap-s1">
              {tags.length === 0 ? (
                <p className={cn(TYPE.small, 'text-ds-muted')}>
                  {t('patientReports.none_in_group')}
                </p>
              ) : (
                tags.map((tag) => (
                  <Checkbox
                    key={tag.id}
                    label={tag.name}
                    checked={section.tagIds.includes(tag.id)}
                    disabled={disabled}
                    onChange={(event) => {
                      const tagIds = event.target.checked
                        ? [...section.tagIds, tag.id]
                        : section.tagIds.filter((id) => id !== tag.id);
                      onChange({ ...section, tagIds, markingIds: [] });
                    }}
                  />
                ))
              )}
            </div>
          </div>
          <Checkbox
            label={t('patientReports.include_quote')}
            checked={section.includeQuote}
            disabled={disabled}
            onChange={(event) => onChange({ ...section, includeQuote: event.target.checked })}
          />
          <Checkbox
            label={t('patientReports.include_comment')}
            checked={section.includeComment}
            disabled={disabled}
            onChange={(event) => onChange({ ...section, includeComment: event.target.checked })}
          />
          <Checkbox
            label={t('patientReports.include_extract')}
            checked={section.includeFileExtract}
            disabled={disabled}
            onChange={(event) => onChange({ ...section, includeFileExtract: event.target.checked })}
          />
        </div>
      ) : null}
      {section.kind === 'questionnaire' ? (
        <div className="mt-s3 flex flex-col gap-s2">
          {formOptions ? (
            <Select
              label={t('patientReports.form_label')}
              disabled={disabled}
              value={section.formsTemplateId ?? ''}
              onChange={(event) => {
                const id = event.target.value;
                const form = formOptions.find((item) => item.id === id);
                onChange({
                  ...section,
                  formsTemplateId: id || null,
                  formsTemplateName: form?.name ?? '',
                  assignmentId: null,
                  allQuestions: true,
                  questionIds: [],
                });
              }}
            >
              <option value="">{t('patientReports.form_none')}</option>
              {formOptions.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.name}
                </option>
              ))}
            </Select>
          ) : null}
          <Checkbox
            label={t('patientReports.part_answers')}
            checked={section.parts.answers}
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...section, parts: { ...section.parts, answers: event.target.checked } })
            }
          />
          <Checkbox
            label={t('patientReports.part_score')}
            checked={section.parts.score}
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...section, parts: { ...section.parts, score: event.target.checked } })
            }
          />
          <Checkbox
            label={t('patientReports.part_charts')}
            checked={section.parts.charts}
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...section, parts: { ...section.parts, charts: event.target.checked } })
            }
          />
          <Checkbox
            label={t('patientReports.part_comments')}
            checked={section.parts.comments}
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...section, parts: { ...section.parts, comments: event.target.checked } })
            }
          />
          <Checkbox
            label={t('patientReports.questions_all')}
            checked={section.allQuestions}
            disabled={disabled}
            onChange={(event) =>
              onChange({
                ...section,
                allQuestions: event.target.checked,
                questionIds: event.target.checked ? [] : section.questionIds,
              })
            }
          />
          {!section.allQuestions && questions.length > 0 ? (
            <div>
              <p className={cn(TYPE.small, 'font-semibold text-ds-text')}>
                {t('patientReports.questions_zoom')}
              </p>
              <div className="mt-s2 flex flex-col gap-s1">
                {questions.map((question) => (
                  <Checkbox
                    key={question.id}
                    label={<span className="line-clamp-2">{question.text}</span>}
                    checked={section.questionIds.includes(question.id)}
                    disabled={disabled}
                    onChange={(event) => {
                      const questionIds = event.target.checked
                        ? [...section.questionIds, question.id]
                        : section.questionIds.filter((id) => id !== question.id);
                      onChange({ ...section, questionIds });
                    }}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {section.kind === 'text' ? (
        <Textarea
          className="mt-s2 min-h-28"
          label={t('patientReports.body')}
          value={section.body}
          disabled={disabled}
          onChange={(event) => onChange({ ...section, body: event.target.value })}
        />
      ) : null}
    </article>
  );
}
